import React, { useEffect, useMemo, useState } from 'react'
import { Provider, useSelector, useDispatch } from 'react-redux'
import clsx from 'clsx'
import { isEmpty, isFinite } from 'lodash-es'

import { store } from 'components/insurance/store/store'
import {
  checkQuoteServiceAvailibility,
  selectInsuranceQuoteInterstitialState,
  setCurrentUser,
  setStartingQuote,
} from '../slice/slice'

import GetQuoteButton from '../button'
import Switch from 'components/utility/switch'
import { formattedMoney } from '../utils'
import { useGetStartingQuote } from '../hooks'
import MonetaryAmount from '../components/MonetaryAmount'

import viaffinityLogo from '/app/assets/images/insurance/logos/viaffinity.png'
import chubbLogo from '/app/assets/images/insurance/logos/chubb.png'
import lemonadeLogo from '/app/assets/images/insurance/logos/lemonade.png'
import loadingIcon from '/app/assets/images/insurance/icons/loader.gif'

const ServerError = () => {
  return (
    <div className="quote-server-error">
      <div className="dome-p2 dome-color-dark-grey">The Chubb rating service is unavailable.</div>
      <div className="dome-p3-lt dome-color-dark-grey" style={{ fontSize: '10px' }}>
        If this problem persists, please contact{' '}
        <a href="mailto:info@domecile.com" className="dome-link dome-link-aqua" style={{ fontSize: '10px' }}>
          info@domecile.com
        </a>
        .
      </div>
    </div>
  )
}

const QuoteLoading = () => {
  return (
    <div className="quote-loading dome-d-flex dome-align-center">
      <div className="dome-loading-img">
        <img src={loadingIcon} width={95} height={95} />
      </div>
      <div>
        <div className="dome-p2 dome-color-dark-grey">We’re still working on the estimate for this property.</div>
        <div className="dome-p3-lt dome-color-dark-grey">In the meantime, continue to the next step.</div>
      </div>
    </div>
  )
}

const QuoteResult = ({ premium, serverError, loading, defaultInsuranceType, handleToggleInsuranceType, hideQuote }) => {
  const renderResult = useMemo(() => {
    if (hideQuote) return

    if (serverError) {
      return <ServerError />
    }

    if (loading || !premium) {
      return <QuoteLoading />
    }

    const parsedPremium = parseInt(premium)
    const monthlyAmount = (isFinite(parsedPremium) ? parsedPremium : 0) / 12
    const formattedMonthlyAmount = formattedMoney(monthlyAmount)

    return (
      <div className="dome-d-flex premium-container">
        <div className="dome-p3 dome-color-med-grey">quote estimate:</div>
        <div className="dome-p1 dome-d-flex dome-align-baseline">
          <div className="dome-h2 dome-color-dark-green dome-d-flex dome-align-start" style={{ lineHeight: 1 }}>
            <MonetaryAmount amount={monthlyAmount} />
          </div>
          <span>/month</span>
        </div>
      </div>
    )
  }, [serverError, loading, premium, hideQuote])

  return (
    <div className="result-container dome-d-flex dome-justify-between">
      {renderResult}
      <div className={clsx("dome-d-flex insurance-switch-container", hideQuote ? 'dome-align-start' : 'dome-align-end')}>
        <Switch
          variant="outlined"
          offText="owner"
          onText="renter"
          onChange={handleToggleInsuranceType}
          on={defaultInsuranceType === 'renters'}
        />
        <div className="dome-d-flex dome-gap3">
          <em className="dome-color-med-grey dome-p4">by</em>
          <img src={viaffinityLogo} style={{ width: '56px', objectFit: 'contain' }} />
        </div>
      </div>
    </div>
  )
}

function EstimatedQuoteResult({
  building,
  building_structure_id,
  package_id,
  expanded,
  closeExpansion,
  insuranceType: defaultInsuranceType,
  package_requirement_titles,
  hideQuote
}) {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [insuranceType, setInsuranceType] = useState(defaultInsuranceType)
  const { quote, fullScreenLoading, serverError } = useSelector(selectInsuranceQuoteInterstitialState)
  const startingQuote = useGetStartingQuote(building, insuranceType)

  useEffect(() => {
    dispatch(setStartingQuote(isEmpty(startingQuote) ? null : startingQuote))
  }, [dispatch, startingQuote])

  const [premium, writingCompany] = useMemo(() => {
    if (quote && quote.insuranceType === insuranceType && quote.buildingId === building?.id) {
      return [quote.premium, quote.writingCompany]
    }

    if (startingQuote) {
      return [startingQuote.premium, startingQuote.writingCompany]
    }

    return [null, null]
  }, [quote, insuranceType, startingQuote, building?.id])

  const [started, setStarted] = useState(premium ? false : expanded)

  useEffect(() => {
    if (premium) return

    setStarted(expanded)

    if (!expanded) {
      setLoading(true)
    }
  }, [expanded])

  useEffect(() => {
    if (fullScreenLoading.visible) {
      setLoading(fullScreenLoading.visible)
    }

    if (!fullScreenLoading.visible && started) {
      setLoading(false)
    }
  }, [fullScreenLoading])

  const onInterstitialClose = () => {
    setStarted(false)
    setLoading(false)
    closeExpansion()
  }
  const onInterstitialOpen = () => {
    setStarted(true)
  }

  const handleToggleInsuranceType = (on) => {
    setInsuranceType(on ? 'renters' : 'homeowners')
  }

  return (
    <div className={clsx('estimated-quote-result__wrapper', expanded && 'expanded')}>
      <div className="estimated-quote-result">
        <QuoteResult
          premium={premium}
          loading={loading}
          serverError={serverError}
          defaultInsuranceType={defaultInsuranceType}
          handleToggleInsuranceType={handleToggleInsuranceType}
          hideQuote={hideQuote}
        />
      </div>
      {expanded && !loading && (
        <div className='insurance-written-by'>
          {writingCompany && !hideQuote && <div className=" dome-color-med-grey">Insurance underwritten by: {writingCompany}</div>}
          <em className='dome-p5-lt dome-color-dark-grey' style={{display: 'inline-block'}}>
            Insurance underwritten by third party providers. This link is provided through a beneficial relationship with Domecile for your convenience and is not an endorsement.
          </em>
        </div>
      )}

      <div style={{ marginTop: '18px' }}>
        <div className="dome-d-flex dome-justify-between dome-align-center">
          <div className="quote-logos">
            <div>
              <em className="dome-color-med-grey">insurance provided by</em>
            </div>
            <div className="dome-d-flex dome-align-center dome-gap6">
              <img src={chubbLogo} style={{ height: '6px', objectFit: 'contain' }} />
              <img src={lemonadeLogo} style={{ height: '10px', objectFit: 'contain' }} />
            </div>
          </div>
          <GetQuoteButton
            building={building}
            building_structure_id={building_structure_id}
            package_id={package_id}
            started={started}
            onInterstitialClose={onInterstitialClose}
            onInterstitialOpen={onInterstitialOpen}
            insuranceType={insuranceType}
            package_requirement_titles={package_requirement_titles}
          />
        </div>
      </div>
    </div>
  )
}

function ApplicationEntrypoint({ application_package, user, insurance_type, package_requirement_titles, hideQuote }) {
  const {
    building,
    building_structure_id,
    package_type,
    hashed_id: package_id,
    friendly_package_type,
  } = application_package

  const [expanded, setExpanded] = useState(true)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setCurrentUser(user))
    dispatch(checkQuoteServiceAvailibility())
  }, [])

  const toggleExpand = () => setExpanded((expanded) => !expanded)
  const handleCloseExpansion = () => {
    if (hideQuote && expanded) return

    setExpanded(false)
  }

  return (
    <div className="insurance-application-entrypoint">
      <div className="dome-d-flex dome-justify-between">
        <div className="dome-p2 dome-text-w500 dome-color-dark-green">
          insurance for your {!!package_type ? friendly_package_type : "package's building"}
        </div>
        <span onClick={toggleExpand} style={{ cursor: 'pointer' }}>
          <i className={clsx('fa-solid', expanded ? 'fa-chevron-up' : 'fa-chevron-down')}></i>
        </span>
      </div>
      <ul className="insurance-intro-list">
        <li>matches property requirements</li>
        <li>personal info not needed for estimate</li>
        <li>policy auto-uploaded to library</li>
      </ul>
      <EstimatedQuoteResult
        building={building}
        building_structure_id={building_structure_id}
        package_id={package_id}
        expanded={expanded}
        closeExpansion={handleCloseExpansion}
        insuranceType={insurance_type}
        package_requirement_titles={package_requirement_titles}
        hideQuote={hideQuote}
      />
    </div>
  )
}

export default (props) => (
  <Provider store={store}>
    <ApplicationEntrypoint {...props} />
  </Provider>
)
